import { Component, OnDestroy, OnInit } from "@angular/core";
import { ControllerService } from "../shared/services/controller.service";
import { ActivatedRoute, Params, Router } from "@angular/router";
import { HttpService } from "../shared/services/http.service";
import { AuthService } from "../auth/auth.service";
import { StoreHelper } from "../shared/helpers/store.helper";
import { ToggleService } from "../shared/services/toggle.service";
import { BunnyMapService } from "../shared/services/bunny-map.service";
import swal from "sweetalert2";


@Component({
    selector: "app-start",
    templateUrl: "./start.component.html",
    styleUrls: ["./start.component.scss"],
    standalone: false
})
export class StartComponent implements OnInit, OnDestroy {
  private readonly livePreviewStorageKey = 'hemisphere.livePreview.content';
  steps = [];
  loading = true;

  qpListener = null
  fastForward = false
  sentActions = false
  activationCode = ''

  constructor(
    private authService: AuthService,
    public controller: ControllerService,
    private httpService: HttpService,
    private route: ActivatedRoute,
    private router: Router,
    private toggleService: ToggleService,
    private bunnyMapService: BunnyMapService
  ) {}

  toggleFastForward(event) {
    StoreHelper.setObject("fastForward", event.target.checked);
  }

  toggleSentActions(event){
    if( event.target.checked ){
      this.controller.setCookie("sentActions", "true", 10)
    }else{
      this.controller.removeCookie("sentActions")
    }
  }

  ngOnDestroy(): void {
    if( this.qpListener ){
      this.qpListener.unsubscribe()

    }
  }

  setupSteps( ){
    let startingSteps = {};
    this.steps = this.controller
      .getSteps()
      .map((it, index) => {
        if (startingSteps[it.componentId]) {
          return false;
        }
        startingSteps[it.componentId] = true;
        return {
          step: index,
          ...it,
      };
      })
      .filter(Boolean);
  }


  onlyAlphaNumeric( inputString: string ){
    return inputString.replace(/[^a-zA-Z0-9]/g, '');
  }

  // Admin preview flows (livePreview/hcontentId/googleDocId) skip user/getContents,
  // which is the only place bunnyMap normally comes from - fetch it separately here.
  private async loadBunnyMapForPreview(): Promise<void> {
    try {
      const response: any = await this.httpService.getAsync('hcontent/getVideos')
      const videos = response?.data || []
      const bunnyMap = videos.reduce((acc: Record<string, string>, { id, bunny_id }: any) => {
        if( id && bunny_id ){
          acc[id] = bunny_id
        }
        return acc
      }, {} as Record<string, string>)
      this.bunnyMapService.setMappings(bunnyMap)
    } catch( e ){
      // preview can still proceed without bunny playback mapping
    }
  }

  async ngOnInit() {
    // Ensure bunny is enabled in every entry path, including admin preview flows
    // (live preview / hcontentId / googleDocId) that never hit user/getContents.
    this.toggleService.setToggles({bunny: true})

    this.fastForward = StoreHelper.getObject("fastForward")


    if( this.activationCode === '' ){
      this.activationCode = StoreHelper.getObject("activationCode")
      StoreHelper.setObject("activationCode", '')
    }

    if( !this.activationCode ){
      this.activationCode = ''
    }

    if( this.activationCode ){
      const result :any = await this.httpService.postAsync("user/ensureCode", {code: this.activationCode})
      if( result.error ){
        swal('Oops...', result.message, 'error')
        this.activationCode = ''
      }
      this.controller.setActivationCode( this.activationCode )
      this.controller.removeCookie("sentActions")
    }


    this.sentActions = this.controller.getCookie("sentActions") === "true"

    this.controller.shiftContainerToMiddle();

    const user = StoreHelper.getObject('currentUser')


    const isHr = user && user.hr

    if( !isHr && this.controller.getPreviewing() ){
      // already loaded, get the steps.

      this.setupSteps()
      this.controller.clearCurrentRoute()

      this.loading = false
      return
    }

    this.qpListener = this.route.queryParams.subscribe(async (params: Params) => {
      const googleDocId = params.googleDocId
      const hcontentId = params.hcontentId
      const livePreview = params.livePreview

      let contentLibraryId = params.contentLibraryId

      if( !contentLibraryId ){
        contentLibraryId = StoreHelper.getObject('contentLibraryId')
      }

      if( livePreview ){
        const previewContent = this.getLivePreviewContent();
        if( !previewContent ){
          this.controller.setErrorMessage("No live preview content was found. Return to the content editor and open preview again.");
          this.loading = false;
          return
        }

        await this.loadBunnyMapForPreview();

        this.controller.loadHemisphere(null, true, false, previewContent);
        this.setupSteps();
        this.controller.clearCurrentRoute()

        const targetStep = params.step != null ? parseInt(params.step) : 0;
        this.controller.setStep(targetStep);

        if (targetStep >= 0) {
          const steps = this.controller.getSteps();
          const step = steps[targetStep];
          if (step) {
            const page = step.subIndex !== undefined ? step.subIndex : (step.index ?? 0);
            this.router.navigate([step.route], { queryParams: { step: targetStep, page } });
            return;
          }
        }

        this.loading = false
        return
      }

      if( hcontentId ){
        const res: any = await this.httpService.postAsync(
          "hcontent/currentContent",
          { hcontentId }
        )

        if (res.success) {
          await this.loadBunnyMapForPreview();

          this.controller.loadHemisphere(null, true, false, res.data);
          this.loading = false;

          this.setupSteps()

          this.controller.setStep(0)

        } else {
          alert("Failed to load content");
        }

        return;

      }

      if (googleDocId) {
        // previewing
        const res: any = await this.httpService.postAsync(
          "hcontent/previewContent",
          { googleDocId }
        );

        if (res.success) {
          await this.loadBunnyMapForPreview();

          this.controller.loadHemisphere(null, true, false, res.data);
          this.loading = false;

          this.setupSteps()

          this.controller.setStep(0)

        } else {
          alert("Failed to load google doc: " + googleDocId);
        }

        return;
      }

      let contentsResponse = null
      try{
        contentsResponse = await this.httpService.postAsync(
          "user/getContents",
          {}
        )
      }
      catch( e ){
      }

      if( !contentsResponse || !contentsResponse.success ){
        this.controller.setErrorMessage("An error occured loading Hemisphere, please wait a moment and then refresh the page to try again.")
        return
      }

      let {data:contents, toggles, bunnyMap} = contentsResponse as any

      this.toggleService.setToggles( {...toggles, bunny:true})

      if (bunnyMap) {
        this.bunnyMapService.setMappings(bunnyMap ?? {})
      }

      await this.controller.fetchContents(contents)

      this.controller.setupLibrary(false)

      if( contentLibraryId ){

        const content = contents.find( ({hcontent_id}) => hcontent_id == contentLibraryId )

        StoreHelper.removeObject('contentLibraryId')


        if( content ){

          this.controller.loadHemisphere(contentLibraryId, false, true )
          await this.controller.loadReflections()

          this.controller.resume(0)
          return
        }else{
          // cant find content just  go to library
          this.controller.goToLibrary()
          return
        }
      }

      if (this.activationCode == '' && this.authService.isAuthenticatedHr()) {
        const clientName = StoreHelper.getCurrentUser().client_name
        this.controller.shiftContainerToTop()
        this.router.navigate([`client/${clientName}/report`]);
        return;
      }


      if (this.activationCode == '' && this.authService.isSuper()) {
        this.router.navigate(["client"]);
        return;
      }

      // get training for activation code, if set.

      const training = contents.find(
        ({ type, total_steps, step, codes }) =>
          type == "training" && (step === null || step < total_steps) && (!this.activationCode || codes.split(',').includes(this.activationCode))
      )

      const completedTraining = contents.find( ({complete}) => complete)

      this.controller.setHaveCompletedATraining( !!completedTraining )

      this.controller.setHaveIncompleteTraining( !!training )

      if( training && !this.activationCode ){
        this.controller.setActivationCode(training.codes.split(',')[0])
      }


      if (!training) {
        this.controller.goToLibrary()
        return
      }

      const { hcontent_id, step } = training;

      if (hcontent_id) {
        this.controller.loadHemisphere(hcontent_id, false, false);
        await this.controller.loadReflections()

        const actions = await this.controller.getTempData("actions")
        if( actions.type || ( actions.items && actions.items.length) ){

          const {items:savedActions}: any = await this.controller.getTempData('actions')

          let actions = this.controller.getActions()

          if( savedActions ){

              const selectedActions = savedActions.reduce( (acc, {title}) => {
                  const action = actions.actions.find( (actionItem) => this.onlyAlphaNumeric(actionItem.title) == title )
                  acc.push(action)
                  return acc
              }, [])
              this.controller.setSelectedActions(selectedActions)
          }

        }

      }

      this.controller.resume(step ? step : 0);
    });
  }

  private getLivePreviewContent(){
    try{
      const raw = localStorage.getItem(this.livePreviewStorageKey)
      return raw ? JSON.parse(raw) : null
    }catch(error){
      console.error('Failed to read live preview content', error)
      return null
    }
  }
}
